import React, { useMemo, useEffect, useState } from 'react';
import { ApolloProvider } from '@apollo/client';
import { Provider as ReduxProvider } from 'react-redux';
import { Provider as PaperProvider } from 'react-native-paper';
import { RootSiblingParent } from 'react-native-root-siblings';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Text } from 'react-native';
import * as Font from 'expo-font';
import {
	FontAwesome,
	Ionicons,
	Foundation,
	SimpleLineIcons,
	MaterialCommunityIcons,
	Entypo,
	Feather,
	MaterialIcons,
} from '@expo/vector-icons';


import { AppContext } from '../common/utils/context';
import { APP_ID, COUNTRY } from '../common/constants';
import createReduxStore from '../common/store';
import { Spinner } from './components/spinner/Spinner';
import ThemeManagerProvider from './theme';
import { BRAND_FONTS } from './design/typography';
import './utils/toastAboveFooter';

import reducers from './reducers';

import { Entry } from './Entry';
import apolloClient from './apolloClient';
import { getServerEndpoint } from './utils';
import { OfflineProvider } from './offline';
import offlineOperationConfigs from './offline/config';

Text.defaultProps = Text.defaultProps || {};
Text.defaultProps.maxFontSizeMultiplier = 1.5;

// Browsers have no setImmediate, and react-native-root-siblings calls it on every
// toast show/hide — without this shim every Toast.show throws on the Expo web build.
// Native runtimes provide setImmediate, so the guard only ever passes on web.
if (typeof setImmediate === 'undefined' && typeof window !== 'undefined') {
	window.setImmediate = (callback, ...args) => setTimeout(callback, 0, ...args);
	window.clearImmediate = (id) => clearTimeout(id);
}

// Never hold the app on the spinner for a slow font download; text falls back
// to the system font and switches to Sofia Pro once it arrives.
const BRAND_FONT_WAIT_MS = 4000;

const App = () => {
	const [isLoading, setIsLoading] = useState(true);
	const [apollo, setApollo] = useState(null);
	const [reduxStore, setReduxStore] = useState(null);
	const [region, setRegion] = useState(COUNTRY.AUSTRALIA);
	const [areBrandFontsLoaded, brandFontsError] = Font.useFonts(BRAND_FONTS);
	const [hasBrandFontWaitExpired, setHasBrandFontWaitExpired] = useState(false);

	useEffect(() => {
		const timeout = setTimeout(() => setHasBrandFontWaitExpired(true), BRAND_FONT_WAIT_MS);
		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		if (brandFontsError) {
			console.warn('Sofia Pro failed to load, using the system font:', brandFontsError);
		}
	}, [brandFontsError]);

	useEffect(() => {
		(async () => {
			await Font.loadAsync({
				...FontAwesome.font,
				...Ionicons.font,
				...Foundation.font,
				...SimpleLineIcons.font,
				...MaterialCommunityIcons.font,
				...Entypo.font,
				...Feather.font,
				...MaterialIcons.font,
			});

			const token = await AsyncStorage.getItem('token');
			const appRegion = await AsyncStorage.getItem('region');
			setRegion(appRegion);
			setReduxStore(createReduxStore({
				...reducers,
			}, {
				pagination: {
					getServiceRuns: {
						sortBy: 'planned',
						sortDirection: -1,
						page: 0,
						perPage: 10,
					},
				},
				auth: {
					isAuthenticated: !!token,
					token,
				},
			}));
			setApollo(await apolloClient);
			setIsLoading(false);
		})();
	}, []);

	const appContextValue = useMemo(() => ({
		appId: APP_ID.MOBILE,
		isMobileApp: true,
		region,
		getServerEndpoint,
	}), [region]);

	const areBrandFontsSettled = areBrandFontsLoaded || Boolean(brandFontsError) || hasBrandFontWaitExpired;

	if (isLoading || !areBrandFontsSettled) {
		return (
			<Spinner />
		);
	}

	return (
		<SafeAreaProvider>
			<AppContext.Provider value={appContextValue}>
				<ApolloProvider client={apollo}>
					<ReduxProvider store={reduxStore}>
						<ActionSheetProvider>
							<RootSiblingParent>
								<PaperProvider>
									<ThemeManagerProvider areBrandFontsLoaded={areBrandFontsLoaded}>
										<OfflineProvider client={apollo} operationConfigs={offlineOperationConfigs}>
											<Entry />
										</OfflineProvider>
									</ThemeManagerProvider>
								</PaperProvider>
							</RootSiblingParent>
						</ActionSheetProvider>
					</ReduxProvider>
				</ApolloProvider>
			</AppContext.Provider>
		</SafeAreaProvider>
	);
};

export default App;
